import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import Image from "mui-image";
import { FullScreenDialog } from "../../core/FullScreenDialog";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";
import {
  useCreateUnlinkedPublisher,
  useGamePublishers,
  usePublisherFitForGameVersioned,
  useShortlistPublisher,
} from "@seabrookstudios/pitch2table";
import LinkOff from "@mui/icons-material/LinkOff";
import { enqueueSnackbar } from "notistack";
import { useWindowSize } from "usehooks-ts";
import { ShortlistPublisherFilterContext, ShortlistPublisherFilterProvider } from "./ShortlistPublisherFilter";
import { PublisherNameSearchField } from "./PublisherNameSearchField";
import { publisherNameMatchesFilter } from "./publisherNameFilter";
import { PublisherBannerIcon } from "../../publishers/ListItem";
import { useMergedPublishers } from "../../publishers/use-merged-publishers";
import { PublisherAdvancedFilters } from "@seabrookstudios/pitch2table-core";
import { AdvancedFilters } from "../../core/AdvancedFilters";
import { useLocalAdvancedFilters } from "../../core/useLocalAdvancedFilters";
import { applyAdvancedFilters } from "../../core/applyAdvancedFilters";
import { FixedSizeList as VirtualList, areEqual } from "react-window";
import { Features } from "../features";
import { CustomPublisherCard } from "./CustomPublisherCard";

const ShortlistLimit = 10;

const PublisherRowBase = ({ index, style, data }) => {
  const { items, checkedSet, togglePublisher, shortlistLimit, fit } = data;
  const [publisher] = items[index];
  const isChecked = checkedSet.has(publisher.id);

  return (
    <div style={style}>
      <ListItem
        key={publisher.id}
        secondaryAction={
          <Checkbox
            edge="end"
            onChange={() => togglePublisher(publisher.id)}
            checked={isChecked}
            disabled={!isChecked && checkedSet.size >= shortlistLimit}
          />
        }
      >
        <ListItemIcon>
          <Image
            src={publisher.logoUrl}
            alt={`Company logo for ${publisher.name}`}
            fit="contain"
            width={32}
            height={32}
            errorIcon={<LinkOff />}
          />
        </ListItemIcon>
        <ListItemText
          primary={publisher.name}
          secondary={publisher.alternateNames.length > 0 ? `AKA ${publisher.alternateNames.join(", ")}` : ""}
        />
        <ListItemIcon>
          <PublisherBannerIcon publisher={publisher} />
        </ListItemIcon>
      </ListItem>
    </div>
  );
};

const PublisherRow = React.memo(PublisherRowBase, areEqual);
PublisherRow.displayName = "PublisherRow";

/**
 * @param {object} props
 * @param {import("@seabrookstudios/types").GameId} props.gameId
 * @param {Function} props.onPublisherSelected
 * @param {string} props.customPublisherName
 * @param {(value: number) => void} props.onDisplayCountChanged
 */
const PublisherList = ({ gameId, onPublisherSelected, customPublisherName, onDisplayCountChanged }) => {
  const { height } = useWindowSize();

  const { filter, setFilter } = useContext(ShortlistPublisherFilterContext);
  const mergedPublishersQuery = useMergedPublishers();
  const { data: existingPublishers = [] } = useGamePublishers(gameId);
  const { data: fit = [] } = usePublisherFitForGameVersioned(gameId, "v4");

  const [checked, setChecked] = React.useState(
    /** @type {import("@seabrookstudios/pitch2table-core").PublisherId[]} */ ([])
  );

  /**
   * @param {import("@seabrookstudios/pitch2table-core").PublisherId} publisherId
   */
  const togglePublisher = useCallback((publisherId) => {
    setChecked((current) => {
      if (current.includes(publisherId)) {
        return current.filter((id) => id !== publisherId);
      }

      return [...current, publisherId];
    });
  }, []);

  useEffect(() => {
    if (customPublisherName === "") {
      return;
    }

    setFilter(customPublisherName);
  }, [customPublisherName, setFilter]);

  const visibleAdvancedFilters = useMemo(
    () => PublisherAdvancedFilters.filter((f) => (f.hideOn ? !f.hideOn.includes("web") : true)),
    []
  );
  const { selectionsByLabel, setSelectionsByLabel } = useLocalAdvancedFilters(visibleAdvancedFilters);

  const advancedFiltered = useMemo(() => {
    const allItems = mergedPublishersQuery.isLoading ? [] : mergedPublishersQuery.data || [];
    return applyAdvancedFilters(allItems, visibleAdvancedFilters, selectionsByLabel);
  }, [mergedPublishersQuery.data, mergedPublishersQuery.isLoading, visibleAdvancedFilters, selectionsByLabel]);

  const existingPublisherIds = useMemo(() => existingPublishers.map((ep) => ep.publisherId), [existingPublishers]);

  const publishersToShow = useMemo(() => {
    const postFilters = advancedFiltered
      .filter(([publisher]) => !existingPublisherIds.includes(publisher.id))
      .filter((tuple) => publisherNameMatchesFilter(tuple, filter));

    if (!Features.PublisherFitForGame) {
      return postFilters;
    }

    postFilters.sort((a, b) => {
      const aFit = fit.find((f) => f.publisherId === a[0].id);
      const bFit = fit.find((f) => f.publisherId === b[0].id);
      const aScore = aFit?.score || 0;
      const bScore = bFit?.score || 0;

      return bScore - aScore;
    });

    return postFilters;
  }, [advancedFiltered, existingPublisherIds, filter, fit]);

  const checkedSet = useMemo(() => new Set(checked), [checked]);

  useEffect(() => {
    onPublisherSelected(checked);
  }, [checked, onPublisherSelected]);

  useEffect(() => {
    onDisplayCountChanged(publishersToShow.length);
  }, [publishersToShow.length, onDisplayCountChanged]);

  useEffect(() => {
    if (checked.length === ShortlistLimit) {
      enqueueSnackbar(`A maximum of ${ShortlistLimit} publishers can be shortlisted at a time`, {
        variant: "info",
        autoHideDuration: 3000,
      });
    }
  }, [checked]);

  const containerHeight = height - 40 - 56;
  const virtualHeight = Math.max(200, containerHeight - 120);

  const itemData = useMemo(
    () => ({
      items: publishersToShow,
      checkedSet,
      togglePublisher,
      shortlistLimit: ShortlistLimit,
      fit,
    }),
    [publishersToShow, checkedSet, togglePublisher, fit]
  );

  return (
    <Box sx={{ height: containerHeight, display: "flex", flexDirection: "column" }}>
      <AdvancedFilters
        filters={visibleAdvancedFilters}
        selectionsByLabel={selectionsByLabel}
        onSelectionsByLabelChange={setSelectionsByLabel}
      />
      <Box p={1}>
        <PublisherNameSearchField id="shortlist-publisher-filter" />
      </Box>

      <Box sx={{ flex: 1, minHeight: 0 }}>
        {mergedPublishersQuery.isLoading ? (
          <CircularProgress />
        ) : (
          <VirtualList
            height={virtualHeight}
            itemCount={publishersToShow.length}
            itemSize={64}
            width="100%"
            itemData={itemData}
          >
            {PublisherRow}
          </VirtualList>
        )}
      </Box>
    </Box>
  );
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/types").GameId} props.gameId
 * @param {boolean} [props.open]
 * @param {() => void} [props.onClose]
 */
export const AddPublisherInterest = ({ gameId, open, onClose }) => {
  const [selected, setSelected] = useState(
    /** @type {import("@seabrookstudios/pitch2table-core").PublisherId[]} */ ([])
  );
  const shortlistPublishers = useShortlistPublisher(gameId);

  /**
   * @param {import("@seabrookstudios/pitch2table-core").PublisherId[]} publisherIds
   */
  const updatedSelectedPublishers = (publisherIds) => {
    setSelected(publisherIds);
  };

  const onAction = async () => {
    if (selected.length === 0) {
      return;
    }

    await shortlistPublishers.mutateAsync({ publisherIds: selected });
  };

  const [newPublisherName, setNewPublisherName] = useState("");

  /**
   * @param {import("@seabrookstudios/pitch2table-core").PublisherForApp} publisher
   */
  const onCreateSuccess = async (publisher) => {
    enqueueSnackbar(`${publisher.name} added to your custom publishers`, { variant: "success" });
    await shortlistPublishers.mutateAsync({ publisherIds: [publisher.id] });
  };
  const create = useCreateUnlinkedPublisher(onCreateSuccess);

  /**
   * @param {string} name
   */
  const handleCustomPublisherCreate = async (name) => {
    await create.mutateAsync({ name });
  };

  return (
    <FullScreenDialog
      title="Shortlist Publishers"
      actionText={selected.length !== 1 ? `SHORTLIST ${selected.length} PUBLISHERS` : "SHORTLIST PUBLISHER"}
      canSave={selected.length === 0}
      onSaveHandler={onAction}
      isBusy={shortlistPublishers.isPending}
      id="add-shortlist-publisher"
      open={open}
      onClose={onClose}
    >
      <Grid container spacing={2}>
        <Grid size={{ xs: 6 }}>
          <Card elevation={2}>
            <ShortlistPublisherFilterProvider>
              <PublisherList
                gameId={gameId}
                onPublisherSelected={updatedSelectedPublishers}
                customPublisherName={newPublisherName}
                onDisplayCountChanged={() => {}}
              />
            </ShortlistPublisherFilterProvider>
          </Card>
        </Grid>
        <Grid size={{ xs: 6 }}>
          <Card elevation={2}>
            <CardContent>
              <Typography>You&apos;re ready to send your game to a publisher. Great news!</Typography>
              <Typography pt={1}>
                The first step is shortlist a set of publishers you wish to contact. From this shortlist you&apos;ll be
                able to record when you have contacted them. You may select at most 10 publishers to shortlist at a
                time.
              </Typography>
            </CardContent>
          </Card>
          <Box p={0.5} />
          <CustomPublisherCard
            onCreate={handleCustomPublisherCreate}
            isPending={create.isPending}
            onNameChange={setNewPublisherName}
          />
        </Grid>
      </Grid>
    </FullScreenDialog>
  );
};
